"use client";
import { format } from "date-fns";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useRelativeTime } from "../hooks/use-relative-time";
import { parseTimestamp } from "../lib/time";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./dialog/popover";
import { toast } from "./toaster";

const timestampLocalFormatter = (value: string | number) => {
  return format(parseTimestamp(value), "MMM dd HH:mm:ss");
};

const timestampLocalHoursWithMillisFormatter = (value: string | number) => {
  return format(parseTimestamp(value), "HH:mm:ss.SSS");
};

const timestampUtcFormatter = (value: string | number) => {
  const isoDate = parseTimestamp(value).toISOString();
  const utcDate = `${isoDate.substring(0, 10)} ${isoDate.substring(11, 19)}`;
  return format(utcDate, "MMM d,yyyy HH:mm:ss");
};

const TooltipRow = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    //biome-ignore lint/a11y/useKeyWithClickEvents: no need
    <span
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
          })
          .catch(() => {
            toast.error("Failed to copy to clipboard");
          });
      }}
      className="flex items-center hover:bg-gray-3 text-left cursor-pointer w-full px-5 py-2"
    >
      <span className="w-32 text-left truncate text-gray-9">{label}</span>
      <span className={cn("ml-2 text-xs text-gray-12", copied ? "text-success-11" : "")}>
        {copied ? "Copied!" : value}
      </span>
    </span>
  );
};

const RelativeTime = ({ value }: { value: string | number }) => {
  return <>{useRelativeTime(value)}</>;
};

const RelativeRow = ({ value }: { value: string | number }) => {
  return <TooltipRow label="Relative" value={useRelativeTime(value)} />;
};

type DisplayType = "local" | "local_hours_with_millis" | "utc" | "relative";

const TimestampInfo: React.FC<{
  value: string | number;
  className?: string;
  displayType?: DisplayType;
  side?: "left" | "right" | "top" | "bottom";
  align?: "start" | "center" | "end";
  triggerRef?: React.RefObject<HTMLElement | null>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}> = ({
  value,
  className,
  displayType = "local",
  side: sideProp,
  align: alignProp,
  triggerRef: externalTriggerRef,
  open,
  onOpenChange,
}: {
  className?: string;
  value: string | number;
  displayType?: DisplayType;
  side?: "left" | "right" | "top" | "bottom";
  align?: "start" | "center" | "end";
  triggerRef?: React.RefObject<HTMLElement | null>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const local = timestampLocalFormatter(value);
  const utc = timestampUtcFormatter(value);
  const [align, setAlign] = useState<"start" | "end">("start");
  const internalTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = externalTriggerRef || internalTriggerRef;
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [internalOpen, setInternalOpen] = useState(false);

  // Use either controlled open state or internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  // When an external trigger element is provided, the popup is anchored to it
  // instead of to our own trigger. The positioner expects a non-null current,
  // so fall back to the body (same pattern as ConfirmPopover).
  const externalAnchor = React.useMemo(() => {
    if (!externalTriggerRef) {
      return undefined;
    }
    return {
      get current() {
        return externalTriggerRef.current ?? document.body;
      },
    };
  }, [externalTriggerRef]);

  useEffect(() => {
    const updateAlignment = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        setAlign(rect.top < windowHeight / 2 ? "start" : "end");
      }
    };
    updateAlignment();
    window.addEventListener("scroll", updateAlignment);
    window.addEventListener("resize", updateAlignment);
    return () => {
      window.removeEventListener("scroll", updateAlignment);
      window.removeEventListener("resize", updateAlignment);
    };
  }, [triggerRef]);

  const getDisplayValue = () => {
    switch (displayType) {
      case "local":
        return local;
      case "utc":
        return utc;
      case "relative":
        return <RelativeTime value={value} />;
      case "local_hours_with_millis":
        return timestampLocalHoursWithMillisFormatter(value);
      default:
        return local;
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {externalTriggerRef ? (
        // An external element controls the popover (via open/onOpenChange) and
        // anchors it, so only render the timestamp text here.
        <span className={cn("text-xs", className)}>{getDisplayValue()}</span>
      ) : (
        // A real, focusable trigger: the popup contains interactive copy rows,
        // so it must be reachable by keyboard. `openOnHover` keeps the old
        // tooltip-like hover behavior; click/Enter/Space also toggle it.
        <PopoverTrigger ref={internalTriggerRef} openOnHover className={cn("text-xs", className)}>
          {getDisplayValue()}
        </PopoverTrigger>
      )}
      <PopoverContent
        align={alignProp ?? align}
        side={sideProp ?? "right"}
        anchor={externalAnchor}
        className="font-mono p-0 text-xs w-auto min-w-[280px] z-50 overflow-hidden"
      >
        <div className="py-3">
          <TooltipRow label="UTC" value={utc} />
          <TooltipRow label={localTimezone} value={local} />
          <RelativeRow value={value} />
          <TooltipRow label="Timestamp" value={String(value)} />
        </div>
      </PopoverContent>
    </Popover>
  );
};
TimestampInfo.displayName = "TimestampInfo";
export { TimestampInfo };
